import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Divider,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface ProjectPlan {
  _id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  project: {
    _id: string;
    name: string;
  };
}

const ScheduleCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projectPlans, setProjectPlans] = useState<ProjectPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importSummary, setImportSummary] = useState<any>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    projectPlanId: '',
    type: 'current' as 'current' | 'baseline' | 'forecast' | 'what_if',
    startDate: dayjs() as Dayjs,
    endDate: dayjs().add(30, 'day') as Dayjs
  });

  useEffect(() => {
    fetchProjectPlans();
  }, []);

  const fetchProjectPlans = async () => {
    try {
      const response = await api.get('/project-plans');
      setProjectPlans(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching project plans:', err);
      setError('Failed to load project plans');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
    setSuccess(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
    setSelectedFile(null);
    setImportSummary(null);
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (file: File) => {
    const allowedTypes = ['.xer', '.xml', '.mpp', '.json'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(fileExt)) {
      setError(`Unsupported file type. Please select a file with one of these extensions: ${allowedTypes.join(', ')}`);
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      setError('File size must be less than 50MB');
      return;
    }
    
    setSelectedFile(file);
    setError(null);
    
    // Auto-fill name if empty
    if (!formData.name) {
      const fileName = file.name.substring(0, file.name.lastIndexOf('.'));
      handleInputChange('name', fileName);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);

    try {
      // Validate form
      if (!formData.name.trim()) {
        throw new Error('Schedule name is required');
      }
      if (!formData.projectPlanId) {
        throw new Error('Project plan is required');
      }

      if (tabValue === 0) {
        // Manual creation
        if (formData.startDate.isAfter(formData.endDate)) {
          throw new Error('End date must be after start date');
        }

        const scheduleData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          projectPlanId: formData.projectPlanId,
          type: formData.type,
          startDate: formData.startDate.toISOString(),
          endDate: formData.endDate.toISOString(),
          status: 'draft'
        };

        const response = await api.post('/schedules', scheduleData);
        setSuccess('Schedule created successfully!');
        
        setTimeout(() => {
          navigate(`/project-planning/gantt/${response.data._id}`);
        }, 1500);
      } else {
        // File upload
        if (!selectedFile) {
          throw new Error('Please select a file to upload');
        }

        const formDataUpload = new FormData();
        formDataUpload.append('scheduleFile', selectedFile);
        formDataUpload.append('name', formData.name.trim());
        formDataUpload.append('description', formData.description.trim());
        formDataUpload.append('projectPlanId', formData.projectPlanId);
        formDataUpload.append('type', formData.type);

        const response = await api.post('/schedules/upload', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total 
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadProgress(progress);
          },
        });

        setImportSummary(response.data.importSummary);
        setSuccess(`Schedule imported successfully! ${response.data.importSummary.tasksImported} tasks imported.`);
        
        setTimeout(() => {
          navigate(`/project-planning/gantt/${response.data.schedule._id}`);
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error creating schedule:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create schedule';
      setError(errorMessage);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = () => {
    navigate('/project-planning');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ScheduleIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Create New Schedule
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {importSummary && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Import Summary:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary={`${importSummary.tasksImported} tasks imported`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary={`${importSummary.dependenciesImported} dependencies imported`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary={`${importSummary.milestonesImported} milestones imported`} />
                </ListItem>
              </List>
            </Alert>
          )}

          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Manual Creation" />
            <Tab label="Import from File" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Schedule Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter schedule name"
              />

              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                disabled={loading}
                placeholder="Enter schedule description (optional)"
              />

              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <FormControl fullWidth required disabled={loading}>
                  <InputLabel>Project Plan</InputLabel>
                  <Select
                    value={formData.projectPlanId}
                    onChange={(e) => handleInputChange('projectPlanId', e.target.value)}
                    label="Project Plan"
                  >
                    {projectPlans.map((plan) => (
                      <MenuItem key={plan._id} value={plan._id}>
                        {plan.name} ({plan.project.name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth disabled={loading}>
                  <InputLabel>Schedule Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    label="Schedule Type"
                  >
                    <MenuItem value="current">Current</MenuItem>
                    <MenuItem value="baseline">Baseline</MenuItem>
                    <MenuItem value="forecast">Forecast</MenuItem>
                    <MenuItem value="what_if">What-If</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {tabValue === 0 ? (
                // Manual Creation Tab
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                  <DatePicker
                    label="Start Date"
                    value={formData.startDate}
                    onChange={(newValue) => handleInputChange('startDate', newValue)}
                    disabled={loading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      }
                    }}
                  />

                  <DatePicker
                    label="End Date"
                    value={formData.endDate}
                    onChange={(newValue) => handleInputChange('endDate', newValue)}
                    disabled={loading}
                    minDate={formData.startDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      }
                    }}
                  />
                </Box>
              ) : (
                // File Upload Tab
                <Box>
                  <Box
                    sx={{
                      border: `2px dashed ${dragActive ? 'primary.main' : 'grey.300'}`,
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: dragActive ? 'action.hover' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-input')?.click()}
                  >
                    <input
                      id="file-input"
                      type="file"
                      accept=".xer,.xml,.mpp,.json"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                      disabled={loading}
                    />
                    
                    {selectedFile ? (
                      <Box>
                        <FileIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        <Chip 
                          label={selectedFile.name.substring(selectedFile.name.lastIndexOf('.'))}
                          color="primary"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    ) : (
                      <Box>
                        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          Drop your schedule file here
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          or click to browse
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supported formats: .xer, .xml, .mpp, .json (max 50MB)
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {uploadProgress > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Uploading: {uploadProgress}%
                      </Typography>
                      <Box sx={{ width: '100%', mt: 1 }}>
                        <div style={{
                          width: '100%',
                          height: 4,
                          backgroundColor: '#e0e0e0',
                          borderRadius: 2
                        }}>
                          <div style={{
                            width: `${uploadProgress}%`,
                            height: '100%',
                            backgroundColor: '#1976d2',
                            borderRadius: 2,
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || (tabValue === 1 && !selectedFile)}
                startIcon={loading ? <CircularProgress size={20} /> : (tabValue === 0 ? <SaveIcon /> : <UploadIcon />)}
              >
                {loading ? (tabValue === 0 ? 'Creating...' : 'Importing...') : (tabValue === 0 ? 'Create Schedule' : 'Import Schedule')}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default ScheduleCreate;